import { useEffect, useRef, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Bank, CheckCircle, CircleNotch, Warning, Pencil } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function PayoutAccountCard() {
  const [banks, setBanks] = useState([]);
  const [account, setAccount] = useState(null); // saved account
  const [editing, setEditing] = useState(false);
  const [accountNumber, setAccountNumber] = useState("");
  const [bankCode, setBankCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(null); // {verified, account_name, bank_name, reason}
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);

  const reload = async () => {
    const [b, a] = await Promise.all([
      api.get("/banks").then((r) => r.data),
      api.get("/agent/payout-account").then((r) => r.data),
    ]);
    setBanks(b);
    if (a?.verified_at) {
      setAccount(a);
      setEditing(false);
      setAccountNumber(a.account_number);
      setBankCode(a.bank_code);
    } else {
      setEditing(true);
    }
  };
  useEffect(() => { reload(); }, []);

  // Auto-verify when both fields populated
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setVerified(null);
    if (accountNumber.length === 10 && bankCode) {
      debounceRef.current = setTimeout(async () => {
        setVerifying(true);
        try {
          const { data } = await api.post("/agent/payout-account/verify", {
            account_number: accountNumber,
            bank_code: bankCode,
          });
          setVerified(data);
        } catch (e) {
          setVerified({ verified: false, reason: formatErr(e?.response?.data?.detail) });
        } finally {
          setVerifying(false);
        }
      }, 600);
    }
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [accountNumber, bankCode]);

  const save = async () => {
    if (!verified?.verified) return;
    setSaving(true);
    try {
      const { data } = await api.post("/agent/payout-account", {
        account_number: accountNumber,
        bank_code: bankCode,
      });
      setAccount(data);
      setEditing(false);
      toast.success("Payout account saved");
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  // Read-only state with verified account on file
  if (account && !editing) {
    return (
      <div className="card border-2 border-[#2D7A5D]/20 bg-[#E2F3EB]/30" data-testid="payout-account-saved">
        <div className="flex items-start justify-between gap-3">
          <div className="flex gap-3 items-start">
            <div className="w-10 h-10 rounded-xl bg-[#2D7A5D] grid place-items-center text-white">
              <Bank size={18} weight="fill" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-display text-base font-medium text-[#132A13]">Payout account verified</span>
                <CheckCircle size={16} weight="fill" className="text-[#2D7A5D]" />
              </div>
              <div className="text-sm text-[#4F5D4F] mt-0.5">{account.bank_name} · ****{account.account_number.slice(-4)}</div>
              <div className="font-mono text-sm font-medium text-[#0A3622] mt-1.5" data-testid="payout-account-name">{account.account_name}</div>
            </div>
          </div>
          <button onClick={() => setEditing(true)} className="btn-ghost text-xs" data-testid="payout-account-edit">
            <Pencil size={12} /> Edit
          </button>
        </div>
        <p className="text-xs text-[#4F5D4F] mt-4">Salary and commission payouts are credited to this NUBAN. Last verified {new Date(account.verified_at).toLocaleDateString()}.</p>
      </div>
    );
  }

  // Edit / first-time setup
  return (
    <div className="card" data-testid="payout-account-form">
      <div className="flex items-center gap-2 overline">
        <Bank size={14} weight="bold" /> Payout account
      </div>
      <h3 className="font-display text-xl font-medium mt-2">Add your NUBAN for payouts</h3>
      <p className="text-sm text-[#4F5D4F] mt-1">Salary and commission earnings are paid here every month.</p>

      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-xs uppercase tracking-[0.14em] text-[#4F5D4F] font-semibold">Bank</span>
          <select className="input mt-1" value={bankCode} onChange={(e) => setBankCode(e.target.value)} data-testid="payout-bank-select">
            <option value="">Select your bank…</option>
            {banks.map((b) => <option key={b.code} value={b.code}>{b.name}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-[0.14em] text-[#4F5D4F] font-semibold">Account number (10 digits)</span>
          <input
            type="text"
            inputMode="numeric"
            maxLength={10}
            className="input mt-1 font-mono"
            placeholder="0123456789"
            value={accountNumber}
            onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ""))}
            data-testid="payout-account-input"
          />
        </label>
      </div>

      {/* Verification feedback */}
      <div className="mt-4 min-h-[44px]" data-testid="payout-verify-feedback">
        {verifying && (
          <div className="flex items-center gap-2 text-sm text-[#4F5D4F]">
            <CircleNotch size={16} className="animate-spin" /> Verifying with your bank…
          </div>
        )}
        {!verifying && verified?.verified && (
          <div className="flex items-center gap-2 text-sm text-[#2D7A5D] bg-[#E2F3EB] rounded-xl p-3" data-testid="payout-verify-success">
            <CheckCircle size={18} weight="fill" />
            <div>
              <div className="font-mono font-medium text-[#0A3622]">{verified.account_name}</div>
              <div className="text-xs">{verified.bank_name || "Verified"} · {verified.provider === "mock" ? "Mock verification" : "Paystack confirmed"}</div>
            </div>
          </div>
        )}
        {!verifying && verified && !verified.verified && (
          <div className="flex items-center gap-2 text-sm text-[#C93B3B] bg-[#FBE3E3] rounded-xl p-3" data-testid="payout-verify-error">
            <Warning size={16} weight="fill" />
            <span>Verification failed: {verified.reason || "Try again"}</span>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-3 mt-5">
        <button
          onClick={save}
          disabled={!verified?.verified || saving}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="payout-save-button">
          {saving ? "Saving…" : "Save payout account"}
        </button>
        {account && (
          <button onClick={() => { setEditing(false); setAccountNumber(account.account_number); setBankCode(account.bank_code); }} className="btn-ghost" data-testid="payout-cancel-button">Cancel</button>
        )}
      </div>
    </div>
  );
}
